import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import PageHeader from './PageHeader';
import exampleStories from '../data/exampleStories';

const SampleGallery = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to hash anchor after page load (e.g. /examples#business-examples)
  useEffect(() => {
    window.scrollTo(0, 0);
    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.replace('#', ''));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [location.hash]);

  // Open demo story in Compiler via localStorage
  const handleOpenDemoInCompiler = (story) => {
    localStorage.setItem('demo_twee_code', story.tweeCode);
    localStorage.setItem('demo_action', 'tryit');
    navigate('/compiler');
  };

  // Group real stories by category prefix
  const personalStories = Object.entries(exampleStories)
    .filter(([key]) => key.startsWith('personal-'))
    .map(([key, val]) => ({ id: key, ...val }));
  const businessStories = Object.entries(exampleStories)
    .filter(([key]) => key.startsWith('business-'))
    .map(([key, val]) => ({ id: key, ...val }));
  const trainingStories = Object.entries(exampleStories)
    .filter(([key]) => key.startsWith('training-'))
    .map(([key, val]) => ({ id: key, ...val }));

  const sampleStories = [
    {
      id: 'educational-quiz',
      title: '🎓 Interactive Science Quiz',
      description: 'Educational branching quiz about the solar system with instant feedback',
      category: 'Education',
      color: 'from-blue-500 to-cyan-500',
      tweeCode: `:: StoryTitle
Interactive Science Quiz

:: Start
<<set $score = 0>>
Welcome to the Solar System Quiz! 🌍

Test your knowledge about our cosmic neighborhood.

**Score: $score / 3**

Let's start with an easy one: **How many planets are in our solar system?**

[[7 planets|Wrong7]]
[[8 planets|Correct8]]
[[9 planets|Wrong9]]

:: Wrong7
Not quite! That was the old count before we discovered more about our solar system.

There are actually **8 recognized planets** (since Pluto was reclassified as a dwarf planet in 2006).

**Score: $score / 3**

[[Try the next question|Question2]]

:: Correct8
<<set $score += 1>>
✅ **Correct!** There are 8 planets in our solar system.

Since 2006, Pluto has been classified as a "dwarf planet."

**Score: $score / 3**

[[Next question →|Question2]]

:: Wrong9
That was true until 2006! Pluto was reclassified as a dwarf planet.

The correct answer is **8 planets**.

**Score: $score / 3**

[[Try the next question|Question2]]

:: Question2
Great! Next question:

**Score: $score / 3**

**Which planet is known as the Red Planet?**

[[Venus|WrongVenus]]
[[Mars|CorrectMars]]
[[Jupiter|WrongJupiter]]

:: WrongVenus
Not quite! Venus is often called Earth's "sister planet" because of its similar size.

The Red Planet is **Mars**, which gets its color from iron oxide (rust) on its surface.

**Score: $score / 3**

[[Continue learning|Question3]]

:: CorrectMars
<<set $score += 1>>
✅ **Perfect!** Mars is called the Red Planet because of the iron oxide on its surface.

You're doing great! 🎉

**Score: $score / 3**

[[Final question →|Question3]]

:: WrongJupiter
Not this time! Jupiter is known as the "Gas Giant."

**Mars** is the Red Planet due to its rusty, iron-rich soil.

**Score: $score / 3**

[[Keep going|Question3]]

:: Question3
Last question! 

**Score: $score / 3**

**Which planet has the most moons?**

[[Saturn|WrongSaturn]]
[[Jupiter|CorrectJupiter]]
[[Neptune|WrongNeptune]]

:: WrongSaturn
Close! Saturn has many moons (over 80), but not the most.

**Jupiter** holds the record with over 90 known moons!

[[See your results|Results]]

:: CorrectJupiter
<<set $score += 1>>
✅ **Excellent!** Jupiter has the most moons - over 90 discovered so far!

[[See your results|Results]]

:: WrongNeptune
Not quite! Neptune has about 14 known moons.

**Jupiter** has the most with over 90 moons!

[[See your results|Results]]

:: Results
🎓 **Quiz Complete!**

**Your Final Score: $score / 3**

<<if $score === 3>>
🌟 **Perfect Score!** You're a space expert!
<<elseif $score === 2>>
⭐ **Great Job!** You know your planets!
<<elseif $score === 1>>
✨ **Good Effort!** Keep learning about space!
<<else>>
💫 **Nice Try!** Want to learn more about our solar system?
<</if>>

[[Start over|Start]]`
    },
    {
      id: 'product-finder',
      title: '☕ Coffee Finder Quiz',
      description: 'E-commerce product recommendation flow for a coffee shop',
      category: 'E-commerce',
      color: 'from-amber-500 to-orange-500',
      tweeCode: `:: StoryTitle
Find Your Perfect Coffee

:: Start
Welcome to **Bean & Brew** Coffee Finder! ☕

Let's discover your perfect coffee match.

**How do you usually drink your coffee?**

[[Black (no milk or sugar)|Black]]
[[With milk or cream|Milk]]
[[Sweet & creamy|Sweet]]

:: Black
Great! You enjoy the pure coffee taste. ☕

**What flavor profile do you prefer?**

[[Bold & strong|BoldBlack]]
[[Smooth & balanced|SmoothBlack]]
[[Light & fruity|LightBlack]]

:: BoldBlack
**Perfect match! We recommend:**

🔥 **Dark Roast Espresso Blend**
- Intense, full-bodied flavor
- Perfect for morning wake-up
- Price: $16.99

[[Shop now|End]]
[[Try another path|Start]]

:: SmoothBlack
**Excellent choice! Try:**

✨ **House Blend Medium Roast**
- Balanced, no bitterness
- Clean finish
- Price: $14.99

[[Shop now|End]]
[[Explore more|Start]]

:: LightBlack
**You'll love:**

🌸 **Ethiopian Light Roast**
- Bright, fruity notes
- Floral aroma
- Price: $17.99

[[Shop now|End]]
[[See other options|Start]]

:: Milk
You like a creamy cup! ☕

**How much milk do you prefer?**

[[Just a splash|Splash]]
[[Half milk, half coffee (latte-style)|Latte]]

:: Splash
**Perfect! We suggest:**

🥛 **Colombian Medium Roast**
- Smooth with milk
- Nutty undertones
- Price: $15.99

[[Add to cart|End]]
[[Browse more|Start]]

:: Latte
**Great for lattes! Try:**

☁️ **Espresso Blend**
- Rich crema
- Sweet with milk
- Price: $16.99

[[Shop now|End]]
[[Keep exploring|Start]]

:: Sweet
You enjoy a treat! 🍰

**Recommended:**

🍮 **Vanilla Caramel Blend**
- Naturally sweet flavor
- Dessert-like
- Price: $18.99

[[Add to cart|End]]
[[See more options|Start]]

:: End
Thank you for using our coffee finder! 

🎁 **Use code FINDER10 for 10% off your first order**

[[Start over|Start]]`
    },
    {
      id: 'safety-training',
      title: '⚠️ Workplace Safety Training',
      description: 'Corporate training scenario for manufacturing safety protocols',
      category: 'Training',
      color: 'from-red-500 to-rose-500',
      tweeCode: `:: StoryTitle
Factory Safety Training

:: Start
**First Day on the Factory Floor** ⚠️

You're starting your first shift at the warehouse. Your supervisor shows you to the forklift area.

"Before you operate the forklift, what should you do first?"

[[Put on my safety vest and hard hat|SafetyGear]]
[[Jump right in—I've watched YouTube tutorials|NoGear]]

:: SafetyGear
✅ **Good choice!** PPE is always the first priority.

Your supervisor nods approvingly. "Now, before starting the forklift, what's next?"

[[Inspect the forklift for damage|Inspect]]
[[Start it up and get to work|SkipInspection]]

:: NoGear
❌ Your supervisor stops you immediately.

"Stop! Personal Protective Equipment is mandatory in this area. Even minor accidents can cause serious injuries without proper gear."

**Let's try again with the right approach.**

[[Put on safety gear|SafetyGear]]

:: Inspect
✅ **Excellent!** Daily inspections prevent 90% of forklift accidents.

You check:
- ✓ Brakes working
- ✓ Horn functional
- ✓ Lights operational
- ✓ Fluid levels good

Everything looks safe!

[[Proceed with caution|Success]]

:: SkipInspection
❌ Your supervisor intervenes again.

"Hold on. OSHA requires daily inspections. Last month, someone skipped this step and discovered their brakes were faulty—mid-operation."

**Safety protocols exist for a reason.**

[[Perform the inspection|Inspect]]

:: Success
🎉 **Training Complete!**

You've successfully demonstrated:
1. ✅ Always wear PPE before entering work areas
2. ✅ Daily equipment inspections save lives
3. ✅ Never skip safety steps to save time

**You're ready for your first shift!**

[[Review training|Start]]`
    },
    {
      id: 'customer-journey',
      title: '🛍️ Interactive Product Demo',
      description: 'Marketing funnel guiding prospects through product features',
      category: 'Marketing',
      color: 'from-purple-500 to-pink-500',
      tweeCode: `:: StoryTitle
Discover SmartHome Pro

:: Start
Welcome to **SmartHome Pro** 🏠

Tired of juggling multiple apps to control your home?

*Imagine controlling everything from one simple app...*

[[Learn more|WhatIs]]
[[See pricing|Pricing]]
[[I'm not interested|NotInterested]]

:: WhatIs
**SmartHome Pro** connects all your devices:

✓ Lights 💡
✓ Thermostat 🌡️
✓ Security 🔒
✓ Entertainment 📺

**One app. Complete control.**

[[How does it work?|HowItWorks]]
[[Show me the benefits|Benefits]]

:: HowItWorks
**Setup in 3 Easy Steps:**

1️⃣ Download the app (2 minutes)
2️⃣ Connect your devices (5 minutes)
3️⃣ Create automation routines (fun!)

No technical skills needed!

[[What are the benefits?|Benefits]]
[[How much does it cost?|Pricing]]

:: Benefits
**Save Time & Money** 💰

✨ Reduce energy bills by 30%
✨ Never leave lights on again
✨ Automate your entire routine

**Real customer:** "I save $85/month on electricity!"

[[I want this!|Pricing]]
[[Tell me more|MoreInfo]]

:: MoreInfo
**Why customers love us:**

⭐⭐⭐⭐⭐ "Changed my life!"
⭐⭐⭐⭐⭐ "So easy to use"
⭐⭐⭐⭐⭐ "Saves me money"

**30-day money-back guarantee**

[[Check pricing|Pricing]]
[[Start free trial|Trial]]

:: Pricing
**SmartHome Pro Plans:**

🏠 **Basic** - $9.99/month
(Control up to 10 devices)

🏡 **Pro** - $19.99/month
(Unlimited devices + AI routines)

🏰 **Family** - $29.99/month
(Everything + Multi-home support)

[[Start free 14-day trial|Trial]]
[[I'm convinced!|Checkout]]

:: Trial
🎉 **Great choice!**

Your 14-day free trial includes:
✓ Full Pro features
✓ No credit card required
✓ Cancel anytime

[[Start my trial now|ThankYou]]

:: Checkout
🛒 **Ready to transform your home!**

Redirecting to secure checkout...

(In a real scenario, this would link to your payment page)

[[Back to start|Start]]

:: NotInterested
No problem! 

Here's a **20% discount code** for when you're ready: **SMARTHOME20**

Thanks for stopping by!

[[Actually, tell me more|WhatIs]]
[[Exit|End]]

:: ThankYou
✅ **You're all set!**

Check your email for setup instructions.

Welcome to the SmartHome family! 🎉

[[Start over|Start]]

:: End
Thanks for visiting SmartHome Pro!

[[Start over|Start]]`
    },
    {
      id: 'mystery-story',
      title: '🔍 The Missing Manuscript',
      description: 'Creative fiction: solve a mystery at the library',
      category: 'Entertainment',
      color: 'from-indigo-500 to-purple-500',
      tweeCode: `:: StoryTitle
The Missing Manuscript

:: Start
**The Library That Wasn't There** 📚

You're researching ancient texts at the university library when you notice a door you've never seen before.

The brass plate reads: *"Special Collections - By Appointment Only"*

The door is slightly ajar...

[[Push the door open|OpenDoor]]
[[Ask the librarian first|AskLibrarian]]
[[Leave and come back later|Leave]]

:: OpenDoor
You push the door open. Inside, dusty shelves hold countless rare books and manuscripts.

In the center of the room, an *ancient leather-bound book* lies open on a reading stand, as if someone just left.

The page shows a hand-drawn map with strange symbols.

[[Examine the map closely|ExamineMap]]
[[Look for the librarian|LookForLibrarian]]
[[Take a photo of the map|TakePhoto]]

:: AskLibrarian
You approach the front desk. The librarian looks up, puzzled.

"Special Collections? There's no such room in this library. You must be mistaken."

But you *definitely* saw the door...

[[Insist you saw it|Insist]]
[[Go back and check|OpenDoor]]

:: ExamineMap
The map shows the university campus, but with buildings that don't exist... or do they?

One building is marked with a red X: **"The Old Observatory - 1887"**

You know the observatory was demolished in the 1960s. Or was it?

[[Search for the observatory|Observatory]]
[[Read more of the manuscript|ReadMore]]

:: LookForLibrarian
You call out, but no one answers. The room feels frozen in time.

Suddenly, you notice the dust on the shelves is *undisturbed* - except for one book that's been recently moved.

[[Pull out the moved book|FindBook]]
[[Leave the room|Leave]]

:: TakePhoto
You pull out your phone, but it's completely dead - even though it was fully charged.

*Strange...*

[[Examine the map without your phone|ExamineMap]]
[[Leave this place|Leave]]

:: Insist
The librarian frowns and checks the computer.

"That's odd... there IS a Special Collections room in the system. Room 217B. But I've worked here for 20 years and never seen it."

[[Show her the door|ShowDoor]]
[[Investigate alone|OpenDoor]]

:: Observatory
You leave the library and head to where the observatory once stood. 

To your surprise, there it is - an old stone building hidden behind overgrown ivy!

**You've discovered something impossible.**

[[Enter the observatory|ObservatoryInside]]
[[Go back to the library|OpenDoor]]

:: ReadMore
You carefully turn the pages. The manuscript describes:

*"The spaces between spaces. Places that exist in memory, waiting to be found again..."*

This is **incredible** research!

[[Take notes|TakeNotes]]
[[Continue reading|MorePages]]

:: FindBook
You pull out the book. Its title: **"Lost Places of the University"**

Inside, there's a bookmark with today's date - but the book looks centuries old.

[[Open to the bookmarked page|Bookmark]]
[[Put it back and leave|Leave]]

:: ShowDoor
You lead the librarian to the corridor. 

The door is gone. Just solid wall.

She looks at you with concern. "Perhaps you need some rest..."

**But you know what you saw.**

[[Search the library at night|NightSearch]]
[[Give up|End]]

:: ObservatoryInside
Inside, everything is perfectly preserved - as if the building exists in a time bubble.

On the desk: a letter addressed to **you**.

[[Read the letter|Letter]]

:: TakeNotes
You document everything. This could be your greatest discovery!

**Achievement Unlocked: The Researcher** 📝

[[Continue exploring|MorePages]]
[[Return tomorrow with equipment|End]]

:: MorePages
The final page contains instructions:

*"To find what's lost, one must be willing to be lost themselves."*

**Deep.**

[[Contemplate this wisdom|End]]

:: Bookmark
The bookmarked page shows a photo of Special Collections - with *you* in the picture, from today.

**How is this possible?**

[[Investigate further|Mystery]]
[[Run away screaming|End]]

:: NightSearch
You return at midnight. The door appears again, glowing faintly...

*This is just the beginning of your adventure...*

**To be continued...**

[[Start over|Start]]

:: Letter
*"Dear Seeker,*

*You found it. The places between places. Not everyone can.*

*The manuscript is yours to protect now.*

*Return it when the next seeker arrives.*

*- The Previous Guardian"*

**You are now the Guardian of Lost Places** 🔮

[[Accept the role|Guardian]]
[[This is too weird|End]]

:: Guardian
🎉 **Congratulations!**

You've become the Guardian of Special Collections.

Your adventure is just beginning...

[[Start over|Start]]

:: Mystery
The mystery deepens...

*Some questions are better left unanswered.*

[[Or are they?|NightSearch]]

:: Leave
You decide this is too strange and leave the library.

But you can't stop thinking about that room...

[[Return to investigate|Start]]

:: End
**Thanks for playing The Missing Manuscript!**

*Did you find all the secrets?*

[[Play again|Start]]`
    }
  ];

  // All tour content now served from backend via UniversalGuidedTour

  return (
    <div className="min-h-screen bg-white">
      <PageHeader borderBottom={true} />

      <main className="container mx-auto px-4 py-12">
        {/* Page headline */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Sample Story Gallery</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore real examples. Click <strong>Open in Compiler</strong> on any story to load it with every step visible — then expand Step 4 and click <strong>Play Story in New Tab</strong> to see it running.
          </p>
        </div>

        {/* ── Personal Stories ─────────────────────────────────────────── */}
        <section id="personal-examples" className="mb-14 scroll-mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-teal-500 rounded-full" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Personal Stories &amp; Creative Projects</h2>
              <p className="text-sm text-slate-500">Share adventures, fiction, and community stories with branching paths</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {personalStories.map((story) => (
              <RealStoryCard key={story.id} story={story} navigate={navigate} />
            ))}
          </div>
        </section>

        {/* ── Business Case Studies ─────────────────────────────────────── */}
        <section id="business-examples" className="mb-14 scroll-mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-indigo-500 rounded-full" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Business Case Studies That Convert</h2>
              <p className="text-sm text-slate-500">Interactive ROI demos and decision-based scenarios for prospects</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {businessStories.map((story) => (
              <RealStoryCard key={story.id} story={story} navigate={navigate} />
            ))}
          </div>
        </section>

        {/* ── Training & Education ──────────────────────────────────────── */}
        <section id="training-examples" className="mb-14 scroll-mt-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-amber-500 rounded-full" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Training, Education &amp; Onboarding</h2>
              <p className="text-sm text-slate-500">Branching scenarios where learners make decisions and see consequences</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {trainingStories.map((story) => (
              <RealStoryCard key={story.id} story={story} navigate={navigate} />
            ))}
          </div>
        </section>

        {/* ── Quick Demos (original inline stories) ─────────────────────── */}
        <section id="quick-demos" className="mb-14 scroll-mt-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 bg-slate-400 rounded-full" />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Quick Demos</h2>
              <p className="text-sm text-slate-500">Short stories to try the Compiler right away</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mb-6 pl-5">
            These demos open directly in the Compiler with the story preloaded.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sampleStories.map((story) => (
              <div
                key={story.id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-indigo-300 hover:shadow-sm transition-all"
              >
                <div className={`h-20 bg-gradient-to-r ${story.color}`} />
                <div className="p-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">{story.category}</p>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{story.title}</h3>
                  <p className="text-slate-500 text-sm mb-4 leading-snug">{story.description}</p>
                  <button
                    type="button"
                    data-testid={`gallery-demo-open-${story.id}`}
                    onClick={() => handleOpenDemoInCompiler(story)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Open in Compiler →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="text-center mt-8 border-t border-slate-200 pt-12">
          <p className="text-slate-600 mb-4">Ready to build your own?</p>
          <Button
            size="lg"
            onClick={() => navigate('/compiler')}
            className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8"
          >
            Start Building Using the Compiler
          </Button>
        </div>
      </main>
    </div>
  );
};

/**
 * RealStoryCard — Card for stories from exampleStories.js (routed to /examples/:id)
 */
const RealStoryCard = ({ story, navigate }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-sm transition-all flex flex-col">
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{story.category}</p>
    <h3 className="text-base font-bold text-slate-900 mb-2 leading-snug">{story.title}</h3>
    <p className="text-slate-500 text-sm leading-snug mb-5 flex-1">{story.description}</p>
    <button
      type="button"
      data-testid={`gallery-open-${story.id}`}
      onClick={() => navigate(`/examples/${story.id}`)}
      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors"
    >
      Open in Compiler →
    </button>
  </div>
);

export default SampleGallery;